// src/page/TestSubmissionAnalysis/TestSubmissionAnalysis.tsx
import React, { useState } from "react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine, faTable, faFilter, faTimes, faSpinner,
  faCheckCircle, faTimesCircle, faMinus, faChevronLeft, faChevronRight,
  faArrowLeft, faTrophy, faLayerGroup, faListAlt,
} from "@fortawesome/free-solid-svg-icons";
import useTestSubmissionAnalysis from "./useTestSubmissionAnalysis";

const DONUT_COLORS = ["#16a34a", "#dc2626", "#f59e0b"];
const BAR_COLORS = ["#1e3a8a", "#3b82f6", "#60a5fa", "#93c5fd", "#bfdbfe", "#dbeafe"];

type Tab = "overview" | "by-series" | "recent";

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard: React.FC<{ label: string; value: string | number; sub?: string; color: string }> = ({ label, value, sub, color }) => (
  <div className={`rounded-xl border p-4 flex flex-col gap-1 ${color}`}>
    <div className="text-2xl font-bold text-fontColor">{value}</div>
    <div className="text-sm font-medium text-gray-600">{label}</div>
    {sub && <div className="text-xs text-gray-400">{sub}</div>}
  </div>
);

// ─── Pagination Bar ───────────────────────────────────────────────────────────
const PaginationBar: React.FC<{
  currentPage: number; totalPages: number; totalCount: number;
  onPrev: () => void; onNext: () => void;
}> = ({ currentPage, totalPages, totalCount, onPrev, onNext }) => (
  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
    <span className="text-xs text-gray-400">
      Page {currentPage} of {totalPages} · {totalCount} records
    </span>
    <div className="flex gap-2">
      <button disabled={currentPage <= 1} onClick={onPrev}
        className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
        <FontAwesomeIcon icon={faChevronLeft} className="mr-1" /> Prev
      </button>
      <button disabled={currentPage >= totalPages} onClick={onNext}
        className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
        Next <FontAwesomeIcon icon={faChevronRight} className="ml-1" />
      </button>
    </div>
  </div>
);

// ─── Test Series Detail Modal ─────────────────────────────────────────────────
const SeriesDetailModal: React.FC<{
  open: boolean;
  loading: boolean;
  detail: any;
  page: number;
  onClose: () => void;
  onPageChange: (p: number) => void;
}> = ({ open, loading, detail, page, onClose, onPageChange }) => {
  const [qPage, setQPage] = useState(1);
  const Q_PER_PAGE = 10;
  if (!open) return null;

  const questions = detail?.questionAnalysis ?? [];
  const totalQPages = Math.ceil(questions.length / Q_PER_PAGE);
  const visibleQ = questions.slice((qPage - 1) * Q_PER_PAGE, qPage * Q_PER_PAGE);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto py-6 px-2">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 transition-colors">
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <div>
              <h2 className="font-bold text-fontColor text-base">
                {detail?.seriesInfo?.testSeriesName ?? "Test Series Detail"}
              </h2>
              <p className="text-xs text-gray-400">
                {detail?.seriesInfo?.courseName} · {detail?.seriesInfo?.standard} · {detail?.seriesInfo?.languageName}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-red-500 transition-colors">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64 text-blue-800">
            <FontAwesomeIcon icon={faSpinner} spin className="text-2xl mr-2" />
            Loading analysis…
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Stats row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
              {[
                { label: "Submissions", value: detail?.overallStats?.totalSubmissions ?? 0, color: "bg-blue-50 border-blue-100" },
                { label: "Avg Score", value: detail?.overallStats?.avgScore ?? 0, color: "bg-indigo-50 border-indigo-100" },
                { label: "Highest", value: detail?.overallStats?.highestScore ?? 0, color: "bg-green-50 border-green-100" },
                { label: "Lowest", value: detail?.overallStats?.lowestScore ?? 0, color: "bg-red-50 border-red-100" },
                { label: "Avg Correct", value: detail?.overallStats?.avgCorrect ?? 0, color: "bg-emerald-50 border-emerald-100" },
                { label: "Avg Wrong", value: detail?.overallStats?.avgIncorrect ?? 0, color: "bg-rose-50 border-rose-100" },
                { label: "Unattempted", value: detail?.overallStats?.avgUnattempted ?? 0, color: "bg-yellow-50 border-yellow-100" },
              ].map((s, i) => (
                <div key={i} className={`rounded-xl border p-3 text-center ${s.color}`}>
                  <div className="text-xl font-bold text-fontColor">{s.value}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Question Analysis */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-sm text-fontColor">
                  Question Analysis <span className="text-gray-400 font-normal">({questions.length} questions · sorted by difficulty)</span>
                </h3>
              </div>
              <div className="overflow-x-auto rounded-xl border border-gray-100">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium">Q#</th>
                      <th className="px-4 py-3 text-left font-medium min-w-[250px]">Question</th>
                      <th className="px-4 py-3 text-center font-medium">Attempts</th>
                      <th className="px-4 py-3 text-center font-medium text-green-700">Correct</th>
                      <th className="px-4 py-3 text-center font-medium text-red-600">Wrong</th>
                      <th className="px-4 py-3 text-center font-medium text-yellow-600">Unattempted</th>
                      <th className="px-4 py-3 text-center font-medium">Accuracy</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {visibleQ.length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-8 text-gray-400">No question data</td></tr>
                    ) : visibleQ.map((q: any, i: number) => {
                      const globalIdx = (qPage - 1) * Q_PER_PAGE + i;
                      return (
                        <tr key={q.questionId} className={i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                          <td className="px-4 py-3 font-mono text-xs text-gray-500">
                            Q{q.questionIndex !== undefined ? q.questionIndex : globalIdx + 1}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 max-w-[300px] truncate" title={q.questionText ? q.questionText.replace(/<[^>]*>?/gm, ' ').replace(/&nbsp;/g, ' ') : ""}>
                            {q.questionText ? (
                              <span dangerouslySetInnerHTML={{ __html: q.questionText.replace(/<[^>]*>?/gm, ' ') }} />
                            ) : (
                              <span className="text-gray-400 italic">No text</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center text-gray-600">{q.totalAttempts}</td>
                          <td className="px-4 py-3 text-center text-green-700 font-medium">{q.correctCount}</td>
                          <td className="px-4 py-3 text-center text-red-600 font-medium">{q.incorrectCount}</td>
                          <td className="px-4 py-3 text-center text-yellow-600 font-medium">{q.unattemptedCount}</td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                                <div
                                  className="h-1.5 rounded-full bg-green-500"
                                  style={{ width: `${q.accuracy}%` }}
                                />
                              </div>
                              <span className="text-xs font-semibold w-8 text-right">{q.accuracy}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {totalQPages > 1 && (
                  <PaginationBar
                    currentPage={qPage} totalPages={totalQPages}
                    totalCount={questions.length}
                    onPrev={() => setQPage((p) => p - 1)}
                    onNext={() => setQPage((p) => p + 1)}
                  />
                )}
              </div>
            </div>

            {/* Accuracy Chart */}
            {questions.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm text-fontColor mb-2">Per-Question Accuracy Chart</h3>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart
                    data={questions.slice(0, 30).map((q: any, i: number) => ({ q: `Q${q.questionIndex !== undefined ? q.questionIndex : i + 1}`, acc: q.accuracy }))}
                    margin={{ top: 0, right: 5, left: -25, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="q" tick={{ fontSize: 9, fill: "#9ca3af" }} interval={4} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 9, fill: "#9ca3af" }} />
                    <Tooltip contentStyle={{ borderRadius: "8px", fontSize: "11px" }}
                      formatter={(v: any) => [`${v}%`, "Accuracy"]} />
                    <Bar dataKey="acc" name="Accuracy %" radius={[3, 3, 0, 0]}>
                      {questions.slice(0, 30).map((_: any, i: number) => (
                        <Cell key={i} fill={questions[i].accuracy >= 60 ? "#16a34a" : questions[i].accuracy >= 30 ? "#f59e0b" : "#dc2626"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <p className="text-xs text-gray-400 mt-1">🟢 ≥60% · 🟡 30–60% · 🔴 &lt;30% accuracy (showing first 30)</p>
              </div>
            )}

            {/* Submissions for this series */}
            <div>
              <h3 className="font-semibold text-sm text-fontColor mb-2">Submissions (newest first)</h3>
              <div className="overflow-x-auto rounded-xl border border-gray-100">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium">Student</th>
                      <th className="px-4 py-3 text-center font-medium">Score</th>
                      <th className="px-4 py-3 text-center font-medium">Total Question</th>
                      <th className="px-4 py-3 text-center font-medium text-green-700">Correct</th>
                      <th className="px-4 py-3 text-center text-red-600">Wrong</th>
                      <th className="px-4 py-3 text-center text-yellow-600">Unattempted</th>
                      <th className="px-4 py-3 text-left">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {(detail?.submissions ?? []).length === 0 ? (
                      <tr><td colSpan={7} className="text-center py-8 text-gray-400">No submissions</td></tr>
                    ) : detail.submissions.map((s: any, i: number) => (
                      <tr key={s.id} className={i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-fontColor text-sm">{s.userName}</div>
                          <div className="text-xs text-gray-400">{s.userMobile}</div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                            {s.score}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center text-gray-600 font-medium">
                          {s.totalMarks}
                        </td>
                        <td className="px-4 py-3 text-center text-green-700 font-medium">{s.correctCount}</td>
                        <td className="px-4 py-3 text-center text-red-600 font-medium">{s.incorrectCount}</td>
                        <td className="px-4 py-3 text-center text-yellow-600 font-medium">{s.unAttemptedCount}</td>
                        <td className="px-4 py-3 text-xs text-gray-400">
                          {new Date(s.submittedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {detail?.pagination?.totalPages > 1 && (
                  <PaginationBar
                    currentPage={page} totalPages={detail.pagination.totalPages}
                    totalCount={detail.pagination.totalCount}
                    onPrev={() => onPageChange(page - 1)}
                    onNext={() => onPageChange(page + 1)}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const TestSubmissionAnalysis: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const {
    loading, data, filterOptions, page, setPage, filters, applyFilter, clearFilters,
    selectedSeriesId, seriesDetailLoading, seriesDetail, seriesDetailPage,
    openSeriesDetail, closeSeriesDetail, changeSeriesDetailPage,
  } = useTestSubmissionAnalysis();

  // Chart data
  const lineData = data?.monthlyData.labels.map((label, i) => ({
    month: label.split(" ")[0],
    submissions: data.monthlyData.submissionCounts[i],
    avgScore: data.monthlyData.avgScores[i],
  })) ?? [];

  const donutData = data
    ? [
        { name: "Correct", value: data.donutData.correct },
        { name: "Incorrect", value: data.donutData.incorrect },
        { name: "Unattempted", value: data.donutData.unattempted },
      ]
    : [];

  const standardData = data?.perStandard.slice(0, 8).map((s) => ({
    name: s.standard.length > 14 ? s.standard.substring(0, 14) + "…" : s.standard,
    count: s.count,
  })) ?? [];

  const courseData = data?.perCourse.slice(0, 8).map((c) => ({
    name: c.courseName.length > 14 ? c.courseName.substring(0, 14) + "…" : c.courseName,
    count: c.count,
  })) ?? [];

  // Tabs config
  const tabs: { id: Tab; label: string; icon: any }[] = [
    { id: "overview", label: "Overview", icon: faChartLine },
    { id: "by-series", label: "By Test Series", icon: faLayerGroup },
    { id: "recent", label: "Recent Submissions", icon: faListAlt },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64 text-blue-800">
        <FontAwesomeIcon icon={faSpinner} spin className="text-3xl mr-3" />
        <span className="text-lg">Loading analysis…</span>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Test series detail modal */}
      <SeriesDetailModal
        open={!!selectedSeriesId}
        loading={seriesDetailLoading}
        detail={seriesDetail}
        page={seriesDetailPage}
        onClose={closeSeriesDetail}
        onPageChange={changeSeriesDetailPage}
      />

      {/* ─── Header ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-bold text-fontColor flex items-center gap-2">
          <FontAwesomeIcon icon={faChartLine} className="text-blue-800" />
          Test Submission Analysis
        </h1>
        <span className="text-sm text-gray-400">
          {data?.pagination.totalCount ?? 0} total submissions
        </span>
      </div>

      {/* ─── Dropdown Filters ─────────────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-gray-600">
          <FontAwesomeIcon icon={faFilter} /> Filters
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          {/* Standard dropdown */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Standard</label>
            <select
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 w-44 bg-white"
              value={filters.standard}
              onChange={(e) => applyFilter("standard", e.target.value)}
            >
              <option value="">All Standards</option>
              {filterOptions.standards.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* Course dropdown */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Course</label>
            <select
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 w-44 bg-white"
              value={filters.courseName}
              onChange={(e) => applyFilter("courseName", e.target.value)}
            >
              <option value="">All Courses</option>
              {filterOptions.courses.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* Test Series dropdown */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Test Series</label>
            <select
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 w-56 bg-white"
              value={filters.testSeriesId}
              onChange={(e) => applyFilter("testSeriesId", e.target.value)}
            >
              <option value="">All Test Series</option>
              {filterOptions.testSeries.map((t) => (
                <option key={String(t.id)} value={String(t.id)}>{t.name}</option>
              ))}
            </select>
          </div>

          {/* Date range — From */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">From Date</label>
            <input
              type="date"
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 w-40 bg-white"
              value={filters.dateFrom}
              max={filters.dateTo || undefined}
              onChange={(e) => applyFilter("dateFrom", e.target.value)}
            />
          </div>

          {/* Date range — To */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">To Date</label>
            <input
              type="date"
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 w-40 bg-white"
              value={filters.dateTo}
              min={filters.dateFrom || undefined}
              onChange={(e) => applyFilter("dateTo", e.target.value)}
            />
          </div>

          {/* Clear */}
          {(filters.standard || filters.courseName || filters.testSeriesId || filters.dateFrom || filters.dateTo) && (
            <button onClick={clearFilters}
              className="text-sm text-gray-500 flex items-center gap-1 hover:text-red-500 transition-colors">
              <FontAwesomeIcon icon={faTimes} /> Clear All
            </button>
          )}
        </div>
      </div>


      {/* ─── Summary Stat Cards ──────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard label="Total Submissions" value={data?.totalSubmissions ?? 0} color="bg-blue-50 border-blue-100" />
        <StatCard label="Avg Score" value={data?.overallStats.avgScore ?? 0} sub="per submission" color="bg-indigo-50 border-indigo-100" />
        <StatCard label="Avg Correct" value={data?.overallStats.avgCorrect ?? 0} sub="questions" color="bg-green-50 border-green-100" />
        <StatCard label="Avg Incorrect" value={data?.overallStats.avgIncorrect ?? 0} sub="questions" color="bg-red-50 border-red-100" />
        <StatCard label="Avg Unattempted" value={data?.overallStats.avgUnattempted ?? 0} sub="questions" color="bg-yellow-50 border-yellow-100" />
      </div>

      {/* ─── Tabs ────────────────────────────────────────────────── */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? "bg-white text-blue-800 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <FontAwesomeIcon icon={tab.icon} className="text-xs" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ─── Tab: Overview ──────────────────────────────────────── */}
      {activeTab === "overview" && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Line chart */}
            <div className="lg:col-span-2 bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
              <h3 className="font-semibold text-sm text-fontColor mb-3">Monthly Submission Trend</h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={lineData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#6b7280" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#6b7280" }} />
                  <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e5e7eb", fontSize: "12px" }} />
                  <Legend wrapperStyle={{ fontSize: "11px" }} />
                  <Line type="monotone" dataKey="submissions" name="Submissions" stroke="#1e3a8a" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="avgScore" name="Avg Score" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="4 2" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Donut chart */}
            <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
              <h3 className="font-semibold text-sm text-fontColor mb-3">Answer Distribution</h3>
              {donutData.every((d) => d.value === 0) ? (
                <div className="flex items-center justify-center h-48 text-gray-400 text-sm">No data</div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={donutData} cx="50%" cy="45%" innerRadius={50} outerRadius={75}
                      dataKey="value"
                      label={({ percent }: { percent?: number }) => `${((percent ?? 0) * 100).toFixed(0)}%`}
                      labelLine={false}>
                      {donutData.map((_, i) => <Cell key={i} fill={DONUT_COLORS[i]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e5e7eb", fontSize: "12px" }} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: "11px" }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Standard + Course bars */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {[
              { title: "Submissions by Standard", chartData: standardData },
              { title: "Submissions by Course", chartData: courseData },
            ].map((chart, ci) => (
              <div key={ci} className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
                <h3 className="font-semibold text-sm text-fontColor mb-3">{chart.title}</h3>
                {chart.chartData.length === 0 ? (
                  <div className="flex items-center justify-center h-48 text-gray-400 text-sm">No data</div>
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={chart.chartData} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 10, fill: "#6b7280" }} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "#6b7280" }} width={90} />
                      <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e5e7eb", fontSize: "12px" }} />
                      <Bar dataKey="count" name="Submissions" radius={[0, 4, 4, 0]}>
                        {chart.chartData.map((_, i) => (
                          <Cell key={i} fill={BAR_COLORS[(i + ci * 2) % BAR_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Tab: By Test Series ────────────────────────────────── */}
      {activeTab === "by-series" && (
        <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
            <FontAwesomeIcon icon={faTable} className="text-blue-800" />
            <h3 className="font-semibold text-sm text-fontColor">Per Test Series Breakdown</h3>
            <span className="ml-auto text-xs text-blue-600 italic">Click a row to view question analysis</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Test Series</th>
                  <th className="px-4 py-3 text-left font-medium">Course</th>
                  <th className="px-4 py-3 text-left font-medium">Standard</th>
                  <th className="px-4 py-3 text-center font-medium">Submissions</th>
                  <th className="px-4 py-3 text-center font-medium">Avg Score</th>
                  <th className="px-4 py-3 text-center font-medium text-green-700">
                    <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />Correct
                  </th>
                  <th className="px-4 py-3 text-center font-medium text-red-600">
                    <FontAwesomeIcon icon={faTimesCircle} className="mr-1" />Wrong
                  </th>
                  <th className="px-4 py-3 text-center font-medium text-yellow-600">
                    <FontAwesomeIcon icon={faMinus} className="mr-1" />Unattempted
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(data?.perTestSeries ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center text-gray-400 py-10">No data found</td>
                  </tr>
                ) : data?.perTestSeries.map((t, i) => (
                  <tr
                    key={t.id}
                    onClick={() => openSeriesDetail(String(t.id))}
                    className={`cursor-pointer hover:bg-blue-50 transition-colors ${i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}
                  >
                    <td className="px-4 py-3 font-medium text-fontColor max-w-[200px] truncate" title={t.name}>
                      <span className="text-blue-700 hover:underline">{t.name}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{t.courseName}</td>
                    <td className="px-4 py-3 text-gray-500">{t.standard}</td>
                    <td className="px-4 py-3 text-center font-semibold text-blue-800">{t.totalSubmissions}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded-full">{t.avgScore}</span>
                    </td>
                    <td className="px-4 py-3 text-center text-green-700 font-medium">{t.avgCorrect}</td>
                    <td className="px-4 py-3 text-center text-red-600 font-medium">{t.avgIncorrect}</td>
                    <td className="px-4 py-3 text-center text-yellow-600 font-medium">{t.avgUnattempted}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── Tab: Recent Submissions ────────────────────────────── */}
      {activeTab === "recent" && (
        <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-sm text-fontColor">Recent Submissions</h3>
            <span className="text-xs text-gray-400">Newest first</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Student</th>
                  <th className="px-4 py-3 text-left font-medium">Test Series</th>
                  <th className="px-4 py-3 text-left font-medium">Standard</th>
                  <th className="px-4 py-3 text-center font-medium">Score</th>
                  <th className="px-4 py-3 text-center font-medium">Total Question</th>
                  <th className="px-4 py-3 text-center font-medium text-green-700">Correct</th>
                  <th className="px-4 py-3 text-center font-medium text-red-600">Wrong</th>
                  <th className="px-4 py-3 text-center font-medium text-yellow-600">Unattempted</th>
                  <th className="px-4 py-3 text-left font-medium">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(data?.recentSubmissions ?? []).length === 0 ? (
                  <tr><td colSpan={9} className="text-center py-10 text-gray-400">No submissions found</td></tr>
                ) : data?.recentSubmissions.map((s, i) => (
                  <tr key={s.id} className={`hover:bg-blue-50 transition-colors ${i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-fontColor">{s.userName}</div>
                      <div className="text-xs text-gray-400">{s.userMobile}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-[160px] truncate" title={s.testSeriesName}>{s.testSeriesName}</td>
                    <td className="px-4 py-3 text-gray-500">{s.standard}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                        {s.score}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600 font-medium">
                      {s.totalMarks}
                    </td>
                    <td className="px-4 py-3 text-center text-green-700 font-medium">{s.correctCount}</td>
                    <td className="px-4 py-3 text-center text-red-600 font-medium">{s.incorrectCount}</td>
                    <td className="px-4 py-3 text-center text-yellow-600 font-medium">{s.unAttemptedCount}</td>
                    <td className="px-4 py-3 text-xs text-gray-400">
                      {new Date(s.submittedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data && data.pagination.totalPages > 1 && (
            <PaginationBar
              currentPage={page} totalPages={data.pagination.totalPages}
              totalCount={data.pagination.totalCount}
              onPrev={() => setPage((p) => p - 1)}
              onNext={() => setPage((p) => p + 1)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default TestSubmissionAnalysis;
